import { FormikProvider } from 'formik';

import { cn, io } from 'lib/helpers/utility';

import {
  usePaymentContext,
  usePaymentContextApi
} from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';
import PayOnlineButton from 'components/Payments/payroc/PayOnlineButton';
import Button from 'components/shared/Button/Button';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import DateInputPicker from 'components/shared/DateInputPicker/DateInputPicker';
import Typography from 'components/shared/Typography/Typography';

import PatientSignature from '../../PatientSignature';
import AuthorizationAndConsent from '../../PreAuthModal/AuthorizationAndConsent';
import PayrocOnlineForm from '../../PreAuthModal/PayrocOnlineForm';
import PreAuthModalForm from '../../PreAuthModal/PreAuthModalForm';
import { validatePreAuthModal } from '../../PreAuthModal/lib/validation';

const InteractivePreAuthBody = ({ formik, appointment, secureTokenId, onCancel, stampDate }) => {
  const { handlePayment } = usePaymentContextApi();
  const { terminal, processor } = usePaymentContext();
  const isPayroc = processor === 'payroc';
  const isSubmitDisabled = !formik.isValid || formik.isSubmitting;

  const handleCheckboxChange = async (field, value) => {
    const wasAlreadyTouched = formik.touched[field];
    await formik.setFieldValue(field, value);
    if (wasAlreadyTouched) {
      await formik.validateField(field);
    }
  };

  const stampNow = () => {
    if (!stampDate) return formik.values.date;
    const next = stampDate();
    formik.setFieldValue('date', next);
    return next;
  };

  const handleSubmit = async () => {
    if (formik.isSubmitting) return;
    formik.setSubmitting(true);
    try {
      const errors = await formik.validateForm();
      if (io(errors)) return;
      const signatureDate = stampNow();
      await handlePayment({
        preAuthForm: {
          ...formik.values,
          date: signatureDate,
          billingAddress: {
            address_ln_1: formik.values.address_ln_1 || '',
            address_ln_2: formik.values.address_ln_2 || '',
            city: formik.values.city || '',
            state: formik.values.state || '',
            zip: formik.values.zip || ''
          }
        }
      });
    } finally {
      formik.setSubmitting(false);
    }
  };

  const handlePayrocSubmit = (data) => {
    const signatureDate = stampNow();
    handlePayment({ ...data, preAuthForm: { ...formik.values, date: signatureDate } });
  };

  const handlePayrocValidate = async () => {
    const ok = await validatePreAuthModal(formik);
    if (ok) stampNow();
    return ok;
  };

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col gap-4">
        {isPayroc ? (
          <PayrocOnlineForm
            key="pre-auth-inline"
            id="pre-auth-inline"
            handlePayment={handlePayrocSubmit}
            appointmentId={appointment?.id}
            amount={appointment?.balance}
            hideButton
            buttonText="Sign"
            validate={handlePayrocValidate}
            mode="tokenization"
            terminalPin={terminal?.pin}
            secureTokenId={secureTokenId}
            content={
              <DateInputPicker
                label="Date"
                name="date"
                parentClassName="w-full"
                value={formik.values.date}
                isClearable={false}
                onChange={(date) => formik.setFieldValue('date', date)}
                headerClassName="pb-0 min-h-fit"
              />
            }>
            <PreAuthModalForm processor={processor} />
          </PayrocOnlineForm>
        ) : (
          <PreAuthModalForm processor={processor}>
            <DateInputPicker
              label="Date"
              name="date"
              parentClassName="w-full"
              value={formik.values.date}
              onChange={(date) => formik.setFieldValue('date', date)}
              isClearable={false}
            />
          </PreAuthModalForm>
        )}
        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-1">
            <Checkbox
              size={16}
              isChecked={formik.values.authorized}
              onChange={(event) => handleCheckboxChange('authorized', event.target.checked)}
              className="w-full max-w-[600px]"
              inputClassName="!mt-1.5"
              innerLabelClassName="items-start"
              labelClassName="text-sm leading-[22px]"
              label="I authorize secure storage and use of my card information according to the terms above for approved healthcare transactions, refunds, and automatic updates."
            />
            {formik.touched.authorized && formik.errors.authorized && (
              <Typography variant="p" size="xs" color="danger-500">
                {formik.errors.authorized}
              </Typography>
            )}
          </div>
          <AuthorizationAndConsent />
        </div>
        <PatientSignature />
        <div className="flex w-full justify-between gap-2 pt-2">
          {onCancel && (
            <Button text="Back" onClick={onCancel} size="small" color="neutral" style="outlined" />
          )}
          {isPayroc ? (
            <div className="flex items-center justify-end">
              <PayOnlineButton className="justify-end" disabled={isSubmitDisabled} />
            </div>
          ) : (
            <Button
              text="Sign"
              onClick={handleSubmit}
              size="small"
              className={cn(isSubmitDisabled && 'pointer-events-none opacity-50')}
              isDisabled={isSubmitDisabled}
            />
          )}
        </div>
      </div>
    </FormikProvider>
  );
};

export default InteractivePreAuthBody;
